import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, CalendarCheck, Send } from 'lucide-react';
import { resolveImageUrl } from '../context/SettingsContext';

const API = process.env.REACT_APP_BACKEND_URL;
const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('de-DE') : '');
const inputCls = 'w-full rounded-lg border border-neutral-200 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300';
export const track = (sid, type, extra = {}) => sid && fetch(`${API}/api/ai/event/${sid}`, {
  method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type, ...extra }),
}).catch(() => {});

export const ProductCards = ({ products, sid, onAsk }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x" data-testid="ai-product-cards">
    {products.map((p) => (
      <div key={p.id} className="snap-start shrink-0 w-52 rounded-xl border border-neutral-200 bg-white overflow-hidden" data-testid={`ai-product-${p.id}`}>
        <div className="h-28 bg-sky-50">{p.image_url && <img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />}</div>
        <div className="p-2.5">
          <div className="text-sm font-bold text-sky-900 leading-tight line-clamp-2">{p.name}</div>
          <div className="text-[11px] text-neutral-500 line-clamp-2 mt-0.5">{p.short}</div>
          <div className="text-sm font-bold text-sky-700 mt-1">{p.price_on_request ? 'Preis auf Anfrage' : `ab ${eur(p.price_1_day)}`}</div>
          <div className="grid gap-1 mt-2">
            <Link to={`/${p.slug || `produkte/${p.id}`}`} onClick={() => track(sid, 'product_click')} className="text-center text-xs font-semibold rounded-full border border-sky-200 py-1 hover:bg-sky-50">Details ansehen</Link>
            <button type="button" onClick={() => onAsk(`Ist "${p.name}" an meinem Wunschtermin verfügbar?`)} className="text-xs font-semibold rounded-full border border-sky-200 py-1 hover:bg-sky-50">Verfügbarkeit prüfen</button>
            {p.available === false
              ? <div className="text-center text-xs font-bold rounded-full bg-neutral-100 text-neutral-500 py-1" data-testid={`ai-booked-${p.id}`}>Im Wunschzeitraum belegt</div>
              : <button type="button" onClick={() => onAsk(`Ich möchte "${p.name}" buchen.`)} className="text-xs font-bold rounded-full bg-yellow-400 text-sky-900 py-1 hover:bg-yellow-500" data-testid={`ai-book-${p.id}`}>Jetzt buchen</button>}
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const ExtrasCard = ({ card, sid, onAsk }) => (
  <div className="space-y-1.5" data-testid="ai-extras-card">
    <div className="text-[11px] font-bold uppercase tracking-wide text-sky-700">{card.title}</div>
    {card.products?.length > 0 && <ProductCards products={card.products} sid={sid} onAsk={onAsk} />}
    {card.service && (
      <Link to={card.service.url} onClick={() => track(sid, 'product_click')} data-testid="ai-extras-service"
        className="flex items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2.5 hover:bg-sky-100 transition-colors">
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-sky-900">{card.service.title}</span>
          <span className="block text-[11px] text-neutral-600">{card.service.text}</span>
        </span>
        <span className="text-xs font-semibold text-sky-800 shrink-0">Ansehen →</span>
      </Link>
    )}
  </div>
);


const rentalDays = (from, to) => (from ? Math.max(1, Math.round((new Date(to || from) - new Date(from)) / 86400000) + 1) : 1);

const EMPTY = { name: '', email: '', phone: '', street: '', zip: '', city: '', delivery: 'pickup_lankow', loc_street: '', loc_zip: '', loc_city: '',
  cleaning: 'kidsevent', setup: false, payment: 'bar', intent: 'binding', privacy: false };
const DRAFT_TTL = 30 * 60 * 1000;
const DRAFT_PREFIX = 'kidsevent_ai_draft_';
export const HANDOVER_KEY = 'kidsevent_ai_handover';
const draftKey = (k) => `${DRAFT_PREFIX}${k}`;
const readDraft = (k) => {
  try {
    const d = JSON.parse(sessionStorage.getItem(draftKey(k)) || 'null');
    if (d && Date.now() - d.ts < DRAFT_TTL) return d;
    sessionStorage.removeItem(draftKey(k));
  } catch { /* noop */ }
  return null;
};
const CUSTOMER_FIELDS = ['name', 'email', 'phone', 'street', 'zip', 'city', 'loc_street', 'loc_zip', 'loc_city', 'payment', 'intent'];
const latestCustomer = () => {
  try {
    const all = Object.keys(sessionStorage).filter((k) => k.startsWith(DRAFT_PREFIX)).map((k) => JSON.parse(sessionStorage.getItem(k) || 'null'))
      .filter((d) => d && Date.now() - d.ts < DRAFT_TTL).sort((a, b) => b.ts - a.ts);
    return all[0] ? Object.fromEntries(CUSTOMER_FIELDS.map((k) => [k, all[0].f?.[k]]).filter(([, v]) => v)) : {};
  } catch { return {}; }
};
export const clearDrafts = () => {
  try { Object.keys(sessionStorage).filter((k) => k.startsWith(DRAFT_PREFIX) || k === HANDOVER_KEY).forEach((k) => sessionStorage.removeItem(k)); } catch { /* noop */ }
};
const isSchwerin = (zip, city) => /schwerin/i.test(city || '') || /^190(5[3-9]|6[0-3])$/.test((zip || '').trim());
const deliveryOption = (f) => (f.delivery === 'pickup_lankow' ? 'pickup_lankow' : (isSchwerin(f.loc_zip, f.loc_city) ? 'delivery_schwerin' : 'delivery_other'));

const doneKey = (k) => `kidsevent_ai_done_${k}`;
const readDone = (k) => { try { return JSON.parse(sessionStorage.getItem(doneKey(k)) || 'null'); } catch { return null; } };
const saveDone = (k, v) => { try { sessionStorage.setItem(doneKey(k), JSON.stringify(v)); } catch { /* noop */ } };

export const BookingCard = ({ card, sid, onCart, cardKey, active = true, sync = null }) => {
  const [draft] = useState(() => readDraft(cardKey));
  const [f, setF] = useState(() => ({ ...EMPTY, ...(draft?.f || latestCustomer()), privacy: false }));
  const [prods, setProds] = useState(draft?.prods || card.products);
  const [dateFrom, setDateFrom] = useState(draft?.dateFrom ?? (card.date_from || ''));
  const [dateTo, setDateTo] = useState(draft?.dateTo ?? (card.date_to || card.date_from || ''));
  const [syncSeq, setSyncSeq] = useState(draft?.syncSeq || 0);
  const [autoCheck, setAutoCheck] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(() => readDone(cardKey));
  useEffect(() => {
    if (done) return;
    const { privacy, ...rest } = f;
    try { sessionStorage.setItem(draftKey(cardKey), JSON.stringify({ f: rest, prods, dateFrom, dateTo, syncSeq, ts: Date.now() })); } catch { /* noop */ }
  }, [f, prods, dateFrom, dateTo, syncSeq, cardKey, done]);
  useEffect(() => {
    if (!active || done || !sync || sync.seq <= syncSeq) return;
    setSyncSeq(sync.seq);
    if (sync.products?.length) setProds(sync.products);
    if (sync.date_from) { setDateFrom(sync.date_from); setDateTo(sync.date_to || sync.date_from); }
    setF((x) => ({
      ...x,
      ...(sync.delivery ? { delivery: sync.delivery === 'pickup' ? 'pickup_lankow' : 'delivery_schwerin' } : {}),
      ...(sync.zip ? { loc_zip: sync.zip } : {}), ...(sync.city ? { loc_city: sync.city } : {}),
      ...(sync.setup != null ? { setup: sync.setup } : {}), ...(sync.cleaning ? { cleaning: sync.cleaning } : {}),
    }));
    setPreview(null);
    setAutoCheck(true);
  }, [sync, active, done, syncSeq]);
  const u = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const delivery = f.delivery !== 'pickup_lankow';
  const setupOffered = prods.some((p) => p.requires_setup);
  const setup = setupOffered && f.setup;
  const cleaningSum = prods.reduce((s, p) => s + Number(p.cleaning_eur || 0), 0);
  const payload = () => ({
    customer_name: f.name.trim(), customer_email: f.email.trim(), customer_phone: f.phone.trim(),
    customer_street: f.street, customer_zip: f.zip, customer_city: f.city,
    event_date_from: dateFrom, event_date_to: dateTo || dateFrom, delivery_option: deliveryOption(f),
    event_location_street: delivery ? f.loc_street : null, event_location_zip: delivery ? f.loc_zip : null, event_location_city: delivery ? f.loc_city : null,
    service_bundle: setup, bundle_mode: setup ? 'both' : 'none', cleaning_option: setup ? null : f.cleaning,
    payment_options: [f.payment], booking_intent: f.intent, privacy_accepted: f.privacy,
    items: prods.map((p) => ({ product_id: p.id, product_name: p.name, rental_duration: rentalDays(dateFrom, dateTo), quantity: 1, unit_price: 0, subtotal: 0 })),
    notes: 'Gebucht über den KI-Assistenten',
  });
  const canPreview = dateFrom && (!delivery || f.loc_zip || f.loc_city);
  const valid = f.name && /\S+@\S+\.\S+/.test(f.email) && f.phone && f.street && f.zip && f.city && dateFrom && f.privacy
    && (!delivery || (f.loc_street && f.loc_zip && f.loc_city));
  const handover = () => {
    const p = payload();
    try {
      sessionStorage.setItem(HANDOVER_KEY, JSON.stringify({ ts: Date.now(), form: {
        customer_name: p.customer_name, customer_email: p.customer_email, customer_phone: p.customer_phone,
        customer_street: p.customer_street, customer_zip: p.customer_zip, customer_city: p.customer_city,
        delivery_option: p.delivery_option, service_bundle: p.service_bundle, bundle_mode: p.bundle_mode,
        ...(p.cleaning_option ? { cleaning_option: p.cleaning_option } : {}), payment_options: p.payment_options, booking_intent: p.booking_intent,
        ...(delivery ? { event_location_street: f.loc_street, event_location_zip: f.loc_zip, event_location_city: f.loc_city, event_location_same_as_billing: false } : {}),
      } }));
    } catch { /* noop */ }
    track(sid, 'form_handover');
    onCart(prods, dateFrom, dateTo);
  };

  useEffect(() => {
    if (!autoCheck) return;
    setAutoCheck(false);
    if (canPreview) check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCheck]);
  const check = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/ai/booking-preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload()) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'Prüfung fehlgeschlagen');
      setPreview(d);
      if (d.cleaning_option && d.cleaning_option !== f.cleaning && !setup) setF((x) => ({ ...x, cleaning: d.cleaning_option }));
      track(sid, 'booking_started');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const submit = async () => {
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/api/bookings`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload()) });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Buchung konnte nicht gespeichert werden');
      setDone(d);
      try { sessionStorage.setItem('lastBookingResult', JSON.stringify(d)); sessionStorage.removeItem(draftKey(cardKey)); } catch { /* noop */ }
      saveDone(cardKey, { id: d.id, total_price: d.total_price, customer_email: d.customer_email, booking_intent: d.booking_intent });
      track(sid, 'booking_done', { booking_id: d.id });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="ai-booking-done">
        <div className="flex items-center gap-2 font-bold text-emerald-800"><CheckCircle2 className="w-5 h-5" /> {done.booking_intent === 'inquiry' ? 'Anfrage gespeichert!' : 'Buchung gespeichert!'}</div>
        <div className="mt-1 text-emerald-900">Nr. <b>{String(done.id).slice(-8).toUpperCase()}</b> · Gesamt {eur(done.total_price)}</div>
        <div className="text-xs text-emerald-800 mt-1">Die Bestätigung kommt per E-Mail an {done.customer_email}.</div>
        <Link to={`/buchungsbestaetigung/${done.id}`} className="inline-block mt-2 text-xs font-bold underline">Zur Buchungsübersicht</Link>
      </div>
    );
  }
  if (!active) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-500" data-testid="ai-booking-card-replaced">
        Buchung: {prods.map((p) => p.name).join(', ')} – durch neuere Auswahl ersetzt
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-sky-200 bg-white p-3 space-y-2 text-sm" data-testid="ai-booking-card">
      <div className="flex items-center gap-2 font-bold text-sky-900"><CalendarCheck className="w-4 h-4" /> Buchung: {prods.map((p) => p.name).join(', ')}</div>
      {syncSeq > 0 && <div className="text-[11px] text-emerald-700 bg-emerald-50 rounded-md px-2 py-1" data-testid="ai-bk-synced">Auswahl aus dem Chat übernommen – Preis unten aktualisiert</div>}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-neutral-500">Von<input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPreview(null); }} className={inputCls} data-testid="ai-bk-from" /></label>
        <label className="text-[11px] text-neutral-500">Bis<input type="date" value={dateTo} min={dateFrom} onChange={(e) => { setDateTo(e.target.value); setPreview(null); }} className={inputCls} data-testid="ai-bk-to" /></label>
      </div>
      <input placeholder="Vor- und Nachname" value={f.name} onChange={u('name')} className={inputCls} data-testid="ai-bk-name" />
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="E-Mail" type="email" value={f.email} onChange={u('email')} className={inputCls} data-testid="ai-bk-email" />
        <input placeholder="Telefon" value={f.phone} onChange={u('phone')} className={inputCls} data-testid="ai-bk-phone" />
      </div>
      <input placeholder="Straße & Nr. (Rechnungsadresse)" value={f.street} onChange={u('street')} className={inputCls} data-testid="ai-bk-street" />
      <div className="grid grid-cols-3 gap-2">
        <input placeholder="PLZ" value={f.zip} onChange={u('zip')} className={inputCls} data-testid="ai-bk-zip" />
        <input placeholder="Ort" value={f.city} onChange={u('city')} className={`${inputCls} col-span-2`} data-testid="ai-bk-city" />
      </div>
      <select value={f.delivery} onChange={(e) => { u('delivery')(e); setPreview(null); }} className={inputCls} data-testid="ai-bk-delivery">
        <option value="pickup_lankow">Selbstabholung (Lankow)</option>
        <option value="delivery_schwerin">Lieferung zum Eventort</option>
      </select>
      {delivery && (
        <div className="grid grid-cols-3 gap-2">
          <input placeholder="Event-Straße" value={f.loc_street} onChange={u('loc_street')} data-testid="ai-bk-loc-street" className={`${inputCls} col-span-3`} />
          <input placeholder="PLZ" value={f.loc_zip} onChange={u('loc_zip')} data-testid="ai-bk-loc-zip" className={inputCls} />
          <input placeholder="Ort" value={f.loc_city} onChange={u('loc_city')} data-testid="ai-bk-loc-city" className={`${inputCls} col-span-2`} />
        </div>
      )}
      <div className="grid grid-cols-1 gap-2">
        {setupOffered && (
          <select value={f.setup ? 'yes' : 'no'} onChange={(e) => { setF({ ...f, setup: e.target.value === 'yes' }); setPreview(null); }} className={inputCls} data-testid="ai-bk-setup">
            <option value="no">Ohne Aufbauservice</option>
            <option value="yes">Mit Auf- & Abbau (inkl. Reinigung)</option>
          </select>
        )}
        {setup ? (
          <div className="text-[11px] text-neutral-600 rounded-lg bg-neutral-50 border border-neutral-200 px-2.5 py-2" data-testid="ai-bk-cleaning-included">Endreinigung ist im Auf- und Abbauservice enthalten</div>
        ) : (
          <select value={f.cleaning} onChange={(e) => { u('cleaning')(e); setPreview(null); }} className={inputCls} data-testid="ai-bk-cleaning">
            <option value="kidsevent">{cleaningSum > 0 ? `Endreinigung durch uns (+${eur(cleaningSum)})` : 'Endreinigung durch uns'}</option>
            <option value="eigenreinigung">Eigenreinigung (0 €)</option>
          </select>
        )}
        <select value={f.payment} onChange={(e) => { u('payment')(e); setPreview(null); }} className={inputCls} data-testid="ai-bk-payment">
          <option value="bar">Bar bei Übergabe</option>
          <option value="vorkasse">Vorkasse (Überweisung/PayPal)</option>
        </select>
      </div>
      <select value={f.intent} onChange={u('intent')} className={inputCls} data-testid="ai-bk-intent">
        <option value="binding">Verbindlich buchen</option>
        <option value="inquiry">Unverbindlich anfragen</option>
      </select>
      <label className="flex items-start gap-2 text-[11px] text-neutral-600">
        <input type="checkbox" checked={f.privacy} onChange={u('privacy')} className="mt-0.5" data-testid="ai-bk-privacy" />
        <span>Ich habe die <a href="/datenschutz" target="_blank" rel="noreferrer" className="underline">Datenschutzbestimmungen</a> zur Kenntnis genommen. *</span>
      </label>
      {preview && (
        <div className="rounded-lg bg-sky-50 border border-sky-100 p-2 text-xs space-y-0.5" data-testid="ai-bk-preview">
          {preview.lines.map((l, i) => <div key={i} className="flex justify-between"><span>{l.name}</span><span>{l.price == null ? 'auf Anfrage' : eur(l.price)}</span></div>)}
          <div className="flex justify-between font-bold border-t border-sky-200 pt-1"><span>Zwischensumme · {fmtDate(dateFrom)}{dateTo && dateTo !== dateFrom ? `–${fmtDate(dateTo)}` : ''}</span><span>{eur(preview.subtotal)}</span></div>
          <div className="text-neutral-500">{preview.note}</div>
        </div>
      )}
      {err && <div className="text-xs text-red-600" data-testid="ai-bk-error">{err}</div>}
      {preview && !valid && <div className="text-[11px] text-neutral-500" data-testid="ai-bk-missing">Für die Buchung bitte noch Kontaktdaten, Adresse und Datenschutz ergänzen.</div>}
      {busy && preview && <div className="text-xs text-sky-700">Buchung wird gespeichert und Bestätigung versendet – einen Moment bitte…</div>}
      <div className="flex gap-2">
        {!preview ? (
          <button type="button" disabled={!canPreview || busy} onClick={check} className="flex-1 rounded-full bg-sky-800 text-white font-bold py-2 disabled:opacity-40" data-testid="ai-bk-check">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Preis & Übersicht'}
          </button>
        ) : (
          <button type="button" disabled={!valid || busy} onClick={submit} className="flex-1 rounded-full bg-yellow-400 text-sky-900 font-bold py-2 disabled:opacity-40" data-testid="ai-bk-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (f.intent === 'binding' ? 'Jetzt verbindlich buchen' : 'Anfrage absenden')}
          </button>
        )}
        <button type="button" onClick={handover} className="rounded-full border border-sky-200 px-3 text-xs font-semibold" data-testid="ai-bk-handover">
          Zur Buchungsseite
        </button>
      </div>
    </div>
  );
};

export const HandoffCard = ({ sid, cardKey }) => {
  const [f, setF] = useState({ name: '', email: '', phone: '', message: '' });
  const [state, setState] = useState(() => (readDone(cardKey) ? 'done' : ''));
  const send = async () => {
    setState('busy');
    try {
      const r = await fetch(`${API}/api/ai/handoff/${sid || 'anon-' + Date.now()}`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      if (r.ok) saveDone(cardKey, true);
      setState(r.ok ? 'done' : 'error');
    } catch { setState('error'); }
  };
  if (state === 'done') return <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800" data-testid="ai-handoff-done">Danke! Deine Nachricht ist bei unserem Team. Wir melden uns so schnell wie möglich – meist innerhalb eines Werktags.</div>;
  return (
    <div className="rounded-xl border border-sky-200 bg-white p-3 space-y-2" data-testid="ai-handoff-card">
      <div className="text-sm font-bold text-sky-900">Nachricht an unser Team</div>
      <input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="ai-ho-name" />
      <div className="grid grid-cols-2 gap-2">
        <input placeholder="E-Mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="ai-ho-email" />
        <input placeholder="Telefon (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} />
      </div>
      <textarea placeholder="Dein Anliegen" rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} data-testid="ai-ho-message" />
      {state === 'error' && <div className="text-xs text-red-600">Bitte Name, E-Mail und Nachricht prüfen.</div>}
      <button type="button" onClick={send} disabled={state === 'busy'} className="w-full rounded-full bg-sky-800 text-white text-sm font-bold py-2 inline-flex items-center justify-center gap-1.5" data-testid="ai-ho-send">
        {state === 'busy' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden
      </button>
    </div>
  );
};
